import { DOCUMENT_TYPE, HealthClaimModeEnum } from '@/constant/E_CLAIM'
import { Form, FormProps } from 'antd'
import { useCreateRequestDocument } from '@/services/healthClaim/useCreateRequestDocument'
import { DocumentRequest } from '@/services/healthClaim/useCreateRequestDocument/types'
import { useParams } from 'react-router-dom'
import { useEffect, useMemo } from 'react'
import {
  RequestDocumentHistory,
  RequestDocumentStatus,
} from '@/services/healthClaim/useRequestDocumentList/types'
import { FieldType } from './types'
import message from '@/libs/message'
import healthClaimFormAtom from '@/recoil/healthClaimForm/atom'
import { useRecoilState } from 'recoil'
import { useQueryClient } from '@tanstack/react-query'
import { useRequestDocumentList } from '@/services/healthClaim/useRequestDocumentList'
import { useSendRequestDocumentNotification } from '@/services/healthClaim/useSendRequestDocumentNotification'
import { ClaimStatusCodeEnum } from '../../HealthClaimPage/ClaimHandlingTable/types'

const DOCUMENT_TYPE_CONFIGS = [
  { key: DOCUMENT_TYPE.ID_CARD, label: 'ภาพถ่ายหรือสำเนาบัตรประชาชน' },
  { key: DOCUMENT_TYPE.BANK_ACCOUNT, label: 'สำเนาหน้าบัญชีธนาคาร' },
  { key: DOCUMENT_TYPE.RECEIPT, label: 'ใบเสร็จ' },
  { key: DOCUMENT_TYPE.MED_CERT, label: 'ใบรับรองแพทย์' },
  { key: DOCUMENT_TYPE.OTHER, label: 'อื่นๆ' },
] as const

const DOCUMENT_TYPE_LABEL_MAP: Record<DOCUMENT_TYPE, string> = DOCUMENT_TYPE_CONFIGS.reduce(
  (acc, current) => {
    acc[current.key] = current.label
    return acc
  },
  {} as Record<DOCUMENT_TYPE, string>,
)

const DOCUMENT_TYPE_NAME_TO_TYPE_MAP: Record<string, DOCUMENT_TYPE> = DOCUMENT_TYPE_CONFIGS.reduce(
  (acc, current) => {
    acc[current.label] = current.key
    return acc
  },
  {} as Record<string, DOCUMENT_TYPE>,
)

const mapDocumentTypeLabel = (documentType: DOCUMENT_TYPE): string =>
  DOCUMENT_TYPE_LABEL_MAP[documentType] || ''

const SupportingDocumentsModalHook = ({
  isModalOpen,
  handleOk,
  handleCancel,
  selectedRecord,
}: {
  isModalOpen: boolean
  handleOk?: () => void
  handleCancel?: () => void
  selectedRecord?: RequestDocumentHistory
}) => {
  const { claimFormId } = useParams<{ claimFormId?: string }>()
  const [form] = Form.useForm<FieldType>()
  const [healthClaim, setHealthClaim] = useRecoilState(healthClaimFormAtom)
  const queryClient = useQueryClient()

  // Page modes
  const isCreateMode = healthClaim.mode === HealthClaimModeEnum.CREATE
  const isEditMode = healthClaim.mode === HealthClaimModeEnum.EDIT
  const isViewMode = healthClaim.mode === HealthClaimModeEnum.VIEW

  // Current status
  const statusCode = healthClaim?.data?.statusCode

  // Record modes from history
  const hasSelectedRecord = !!selectedRecord
  const isUploadedRecord = selectedRecord?.status === 'UPLOADED'
  const isPendingRecord = selectedRecord?.status === 'PENDING'
  // Read-only when viewing history (both UPLOADED and PENDING)
  const isReadOnly = hasSelectedRecord

  // Can resend SMS only for PENDING record when not in create mode
  const canResendSms = isPendingRecord && !isCreateMode

  // Disable phone number input when editing a PENDING record
  const cannotEditPhoneNumber = isPendingRecord && isCreateMode

  // For check that checkbox is checked or not in new request mode
  const { data: requestDocumentList } = useRequestDocumentList(claimFormId || '')

  const combinedRequestHistory = useMemo(() => {
    const pendingRecords = healthClaim.pendingRequestDocuments ?? []
    const apiRecords = requestDocumentList ?? []

    if (apiRecords.length === 0 && pendingRecords.length === 0) {
      return [] as RequestDocumentHistory[]
    }

    return [...apiRecords, ...pendingRecords]
  }, [requestDocumentList, healthClaim.pendingRequestDocuments])

  // Map document type names to DOCUMENT_TYPE enum values
  const mapDocumentTypeName = (documentTypeName: string): DOCUMENT_TYPE | null =>
    DOCUMENT_TYPE_NAME_TO_TYPE_MAP[documentTypeName] || null

  // Compute latest status by document type across all history
  const latestStatusByType = useMemo(() => {
    if (!combinedRequestHistory.length)
      return {} as Partial<Record<DOCUMENT_TYPE, RequestDocumentStatus>>

    const map: Partial<Record<DOCUMENT_TYPE, RequestDocumentStatus>> = {}
    const sorted = [...combinedRequestHistory].sort(
      (a, b) => new Date(a.requestDate).getTime() - new Date(b.requestDate).getTime(),
    )
    sorted.forEach((record) => {
      record.documentDetails?.forEach((doc) => {
        const type = mapDocumentTypeName(doc.documentTypeName)
        if (type) map[type] = record.status
      })
    })
    return map
  }, [combinedRequestHistory])

  // All document types that currently have latest status = PENDING
  const disabledTypes = useMemo(() => {
    return new Set(
      Object.entries(latestStatusByType)
        .filter(([, status]) => status === 'PENDING')
        .map(([type]) => type as DOCUMENT_TYPE),
    )
  }, [latestStatusByType])

  const checkboxItems = DOCUMENT_TYPE_CONFIGS

  // Effect to populate form when modal opens or selectedRecord changes
  useEffect(() => {
    if (!isModalOpen) return
    if (hasSelectedRecord && selectedRecord) {
      const formValues: FieldType = {
        phoneNumber: selectedRecord.phoneNumber,
        requestDetail: {},
      }

      // Set checkboxes and request details based on documentDetails
      selectedRecord.documentDetails?.forEach((doc) => {
        const documentType = mapDocumentTypeName(doc.documentTypeName)
        if (documentType) {
          formValues[documentType] = true
          if (formValues.requestDetail) {
            formValues.requestDetail[documentType] = doc.requestDetail
          }
        }
      })

      form.setFieldsValue(formValues)
    } else {
      // New request – reset and prefill phone from health claim detail if available
      form.resetFields()
      const defaultPhone = healthClaim.data?.mobileNumber || ''
      form.setFieldsValue({ phoneNumber: defaultPhone })
    }
  }, [isModalOpen, selectedRecord, hasSelectedRecord, form, healthClaim.data?.mobileNumber])

  // Form submit handler
  const onSubmit = () => {
    if (!isReadOnly) {
      form.submit()
    }
  }

  // Close modal and reset form
  const onClose = (): void => {
    // Always reset to avoid carrying over values between modes
    form.resetFields()
    handleCancel?.()
  }

  const textAlertInfo = 'การส่งขอเอกสารเพิ่มเติม จะถูกส่งหลังจากมีการบันทึกสร้างเคส'
  const textAlertWarning =
    'ข้อความระวัง : รายละเอียดในการขอเอกสารเพิ่มเติมจะไปแสดงในแอปพลิเคชันกรุณาใช้คำกระชับ และสุภาพ'
  const showAlertWarning = !hasSelectedRecord
  const showAlertInfo = isCreateMode && !hasSelectedRecord

  // ใช้ useWatch เพื่อให้ตรวจค่าได้ตลอดเมื่อฟอร์มมีการเปลี่ยนแปลง
  const idCardChecked = Form.useWatch(DOCUMENT_TYPE.ID_CARD, form)
  const bankAccountChecked = Form.useWatch(DOCUMENT_TYPE.BANK_ACCOUNT, form)
  const receiptChecked = Form.useWatch(DOCUMENT_TYPE.RECEIPT, form)
  const medCertChecked = Form.useWatch(DOCUMENT_TYPE.MED_CERT, form)
  const otherChecked = Form.useWatch(DOCUMENT_TYPE.OTHER, form)
  const requestDetail = Form.useWatch('requestDetail', form) as
    | Record<string, string | undefined>
    | undefined
  const phoneNumber = Form.useWatch('phoneNumber', form) as string | undefined

  const checkedMap: Partial<Record<DOCUMENT_TYPE, boolean>> = {
    [DOCUMENT_TYPE.ID_CARD]: idCardChecked,
    [DOCUMENT_TYPE.BANK_ACCOUNT]: bankAccountChecked,
    [DOCUMENT_TYPE.RECEIPT]: receiptChecked,
    [DOCUMENT_TYPE.MED_CERT]: medCertChecked,
    [DOCUMENT_TYPE.OTHER]: otherChecked,
  }

  const { mutateAsync: mutateRequestDocument } = useCreateRequestDocument()
  const { mutateAsync: mutateSendRequestDocumentNotification } =
    useSendRequestDocumentNotification()

  // send/save button disabled only for new requests with incomplete data
  const isSubmitDisabled = hasSelectedRecord
    ? false
    : (() => {
        // At least one allowed (not disabled) type must be checked
        const hasAnyCheckedAllowed = Object.entries(checkedMap).some(
          ([key, checked]) => checked && !disabledTypes.has(key as DOCUMENT_TYPE),
        )
        if (!hasAnyCheckedAllowed) return true
        // All checked allowed types must have details
        for (const key of Object.values(DOCUMENT_TYPE)) {
          if (checkedMap[key] && !disabledTypes.has(key as DOCUMENT_TYPE) && !requestDetail?.[key])
            return true
        }
        if (!phoneNumber) return true
        return false
      })()

  const onFinish: FormProps<FieldType>['onFinish'] = async (values) => {
    const allowedEntries = Object.entries(checkedMap).filter(
      ([key, checked]) => checked && !disabledTypes.has(key as DOCUMENT_TYPE),
    )

    if (allowedEntries.length === 0) {
      message.error('ไม่สามารถขอเอกสารซ้ำได้ เพราะมีคำขอค้างอยู่')
      return
    }

    const buildDocumentRequests = () =>
      allowedEntries.map(([key]) => ({
        documentTypeCode: key,
        requestDetail: requestDetail?.[key] as string,
      }))

    const buildDocumentDetails = () =>
      allowedEntries.map(([key]) => ({
        documentTypeId: key,
        documentTypeName: mapDocumentTypeLabel(key as DOCUMENT_TYPE),
        requireCertifiedCopy: false,
        requestDetail: (requestDetail?.[key] as string) || '',
      }))

    // MODE CREATE: ยังไม่ส่ง SMS ตอนนี้ เก็บสถานะไว้ แล้วให้ส่งหลังบันทึกเคส
    // MODE EDIT: ถ้าหากเป็นสถานะ PENDING_APPROVAL ให้เก็บสถานะไว้ แล้วให้ส่งหลังบันทึกเคส
    const isPendingApproval = statusCode === ClaimStatusCodeEnum.PENDING_APPROVAL
    if ((isCreateMode && !hasSelectedRecord) || isPendingApproval) {
      const newPendingHistory: RequestDocumentHistory = {
        RequestDocumentHistoryId: `local-${Date.now()}`,
        documentDetails: buildDocumentDetails(),
        requestBy: '-',
        phoneNumber: values.phoneNumber || '',
        status: 'PENDING',
        requestDate: new Date().toISOString(),
        uploadedDate: '',
      }

      setHealthClaim((prev) => ({
        ...prev,
        hasRequestedDocuments: true,
        pendingRequestDocuments: [...(prev.pendingRequestDocuments ?? []), newPendingHistory],
      }))
      form.resetFields()
      handleOk?.()
      message.success('บันทึกข้อมูลสำเร็จ')
      return
    }

    // MODE EDIT/VIEW (รายละเอียดเคส): ส่งคำขอเอกสารทันที
    const request: {
      claimFormId: string
      phoneNumber: string
      documentRequests: DocumentRequest[]
    } = {
      claimFormId: claimFormId!,
      phoneNumber: values.phoneNumber!,
      documentRequests: buildDocumentRequests(),
    }

    try {
      await mutateRequestDocument(request)
      // Mark that there are changes affecting save button state
      setHealthClaim((prev) => ({
        ...prev,
        hasRequestedDocuments: true,
        pendingRequestDocuments: [],
      }))
      // Refresh history table
      if (claimFormId) {
        queryClient.invalidateQueries({ queryKey: ['useRequestDocumentList', claimFormId] })
        queryClient.refetchQueries({
          queryKey: ['useGetClaimDetail', claimFormId],
        })
      }
      form.resetFields()
      handleOk?.()
      message.success('ส่งคำขอเอกสารเพิ่มเติมสำเร็จ')
    } catch (error) {
      throw error
    }
  }

  // Resend SMS notification for PENDING record from history
  const onResend = async () => {
    if (!hasSelectedRecord || !selectedRecord || !claimFormId) return
    try {
      await mutateSendRequestDocumentNotification({
        claimFormId,
        phoneNumber,
      }).then(() => {
        // Ensure detail is fresh when navigating
        queryClient.refetchQueries({
          queryKey: ['useGetClaimDetail', healthClaim.data?.claimFormId],
        })
      })
      // Refresh history table
      if (claimFormId) {
        queryClient.invalidateQueries({ queryKey: ['useRequestDocumentList', claimFormId] })
      }
      handleOk?.()
      message.success('ส่งคำขอเอกสารเพิ่มเติมสำเร็จ')
    } catch (error) {
      throw error
    }
  }

  return {
    onClose,
    onSubmit,
    isSubmitDisabled,
    form,
    onFinish,
    textAlertInfo,
    textAlertWarning,
    showAlertInfo,
    showAlertWarning,
    checkboxItems,
    isReadOnly,
    isCreateMode,
    isEditMode,
    isViewMode,
    hasSelectedRecord,
    isUploadedRecord,
    isPendingRecord,
    onResend,
    disabledTypes,
    canResendSms,
    cannotEditPhoneNumber,
  }
}

export default SupportingDocumentsModalHook
